import { RiskStatus, ImpactRisk } from '../types';

/* ── Adaptive float thresholds ── */

export const DEFAULT_THRESHOLDS = {
  CRITICAL: 0,
  WATCH: 5,
};

export interface FloatThresholds {
  critical: number;
  watch: number;
  is_distressed: boolean;
  negative_float_count: number;
  total_activities: number;
  negative_pct: number;
}

const DEFAULT_FLOAT_THRESHOLDS: FloatThresholds = {
  critical: DEFAULT_THRESHOLDS.CRITICAL,
  watch: DEFAULT_THRESHOLDS.WATCH,
  is_distressed: false,
  negative_float_count: 0,
  total_activities: 0,
  negative_pct: 0,
};

export function calculateAdaptiveThresholds(
  activities: { total_float: number }[]
): FloatThresholds {
  const total = activities.length;
  if (total === 0) return { ...DEFAULT_FLOAT_THRESHOLDS };

  const floats = activities.map(a => a.total_float);
  const negative_count = floats.filter(f => f <= 0).length;
  const negative_pct = (negative_count / total) * 100;
  const is_distressed = negative_pct > 20;

  if (!is_distressed) {
    return {
      critical: DEFAULT_THRESHOLDS.CRITICAL,
      watch: DEFAULT_THRESHOLDS.WATCH,
      is_distressed: false,
      negative_float_count: negative_count,
      total_activities: total,
      negative_pct,
    };
  }

  const min_float = Math.min(...floats);
  const max_float = Math.max(...floats);
  const float_range = max_float - min_float;

  const critical_threshold = float_range > 0
    ? Math.round(min_float + float_range * 0.20)
    : min_float;
  const watch_threshold = float_range > 0
    ? Math.round(min_float + float_range * 0.40)
    : min_float + 5;

  return {
    critical: critical_threshold,
    watch: watch_threshold,
    is_distressed: true,
    negative_float_count: negative_count,
    total_activities: total,
    negative_pct,
  };
}

// Critical is exclusively owned by the backend is_critical field (longest path /
// worst float definition) — this only distinguishes Watch vs Healthy for activities
// the backend has already ruled not critical.
export function getFloatTier(
  totalFloat: number,
  thresholds: FloatThresholds = DEFAULT_FLOAT_THRESHOLDS
): 'Watch' | 'Healthy' {
  if (totalFloat <= thresholds.watch) return 'Watch';
  return 'Healthy';
}

// Status pill colours — Flotillo spec, exact: Critical/At risk, Watch/Stale, On track.
export function getFloatColor(tier: 'Critical' | 'Watch' | 'Healthy'): {
  bg: string; text: string; border: string;
} {
  if (tier === 'Critical') return { bg: 'var(--crit-bg)', text: 'var(--crit-text)', border: 'var(--crit-text)' };
  if (tier === 'Watch')    return { bg: 'var(--watch-bg)', text: 'var(--watch-text)', border: 'var(--watch-border)' };
  return                          { bg: 'var(--ok-bg)', text: 'var(--ok-text)', border: 'var(--ok-text)' };
}

export function getFloatBadgeText(tier: 'Critical' | 'Watch' | 'Healthy'): string {
  return tier;
}

export function getFloatRisk(floatDays: number | undefined | null): RiskStatus {
  if (floatDays == null) return 'on_track';
  if (floatDays <= 0) return 'at_risk';
  if (floatDays < 7) return 'watch';
  return 'on_track';
}

export function getFloatLabel(floatDays: number | undefined | null): string {
  if (floatDays == null) return 'Unknown';
  if (floatDays < 0) return 'Delayed';
  if (floatDays === 0) return 'Critical';
  return `${floatDays}d float`;
}

export interface RiskColors {
  bg: string;
  text: string;
  border: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
}

export function getRiskColors(risk: RiskStatus | ImpactRisk | string): RiskColors {
  const normalized = risk?.toLowerCase();
  if (normalized === 'at_risk' || normalized === 'high') {
    return {
      bg: 'var(--crit-bg)',
      text: 'var(--crit-text)',
      border: 'var(--crit-text)',
      bgClass: 'bg-crit-bg',
      textClass: 'text-crit-text',
      borderClass: 'border-crit-text',
    };
  }
  if (normalized === 'watch' || normalized === 'medium') {
    return {
      bg: 'var(--watch-bg)',
      text: 'var(--watch-text)',
      border: 'var(--watch-border)',
      bgClass: 'bg-watch-bg',
      textClass: 'text-watch-text',
      borderClass: 'border-watch-border',
    };
  }
  return {
    bg: 'var(--ok-bg)',
    text: 'var(--ok-text)',
    border: 'var(--ok-text)',
    bgClass: 'bg-ok-bg',
    textClass: 'text-ok-text',
    borderClass: 'border-ok-text',
  };
}

export function getRiskLabel(risk: RiskStatus | ImpactRisk | string): string {
  switch (risk?.toLowerCase()) {
    case 'at_risk': return 'At Risk';
    case 'watch': return 'Watch';
    case 'on_track': return 'On Track';
    case 'high': return 'High Risk';
    case 'medium': return 'Medium Risk';
    case 'low': return 'Low Risk';
    default: return risk || 'Unknown';
  }
}

export function getWorstRisk(risks: RiskStatus[]): RiskStatus {
  if (risks.includes('at_risk')) return 'at_risk';
  if (risks.includes('watch')) return 'watch';
  return 'on_track';
}

export function getPortfolioDescription(
  atRisk: number,
  watch: number,
  onTrack: number,
  total: number
): string {
  if (total === 0) return 'No projects loaded.';
  if (atRisk > 0) {
    return `${atRisk} of ${total} project${total > 1 ? 's' : ''} ${atRisk > 1 ? 'are' : 'is'} at risk and need immediate attention.`;
  }
  if (watch > 0) {
    return `${watch} of ${total} project${total > 1 ? 's' : ''} ${watch > 1 ? 'are' : 'is'} on watch. Monitor closely.`;
  }
  return `All ${total} project${total > 1 ? 's are' : ' is'} on track. Portfolio looks healthy.`;
}

export function getProjectDescription(
  riskStatus: RiskStatus,
  floatDays: number,
  projectName: string
): string {
  if (riskStatus === 'at_risk') {
    return `${projectName} is at risk with only ${floatDays} float day${floatDays !== 1 ? 's' : ''} remaining. Immediate action required.`;
  }
  if (riskStatus === 'watch') {
    return `${projectName} is on watch with ${floatDays} float day${floatDays !== 1 ? 's' : ''} remaining. Monitor closely.`;
  }
  return `${projectName} is on track with ${floatDays} float day${floatDays !== 1 ? 's' : ''} of schedule buffer.`;
}
